import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Alert, Stack,
} from '@mui/material';
import { AdmissionCycle } from '../../types/admissions';

export type CycleAction = 'open' | 'close' | 'reopen' | 'archive' | 'delete';

interface Props {
  open: boolean;
  cycle: AdmissionCycle;
  action: CycleAction;
  onCancel: () => void;
  onConfirm: () => void;
  /** Why the server refused the action (e.g. what to fix before opening). */
  problems?: string[];
  /** The action is running. */
  busy?: boolean;
}

/**
 * One confirmation dialog for every cycle action. Each gets its own tone +
 * warning text — admins should know they're about to invalidate a public
 * link (archive) or hard-delete a draft.
 */
const CycleActionsConfirmDialog: React.FC<Props> = ({ open, cycle, action, onCancel, onConfirm, problems, busy }) => {
  const channels = `online form ${cycle.intake?.online ? 'on' : 'paused'}, walk-ins ${cycle.intake?.walkIn ? 'on' : 'stopped'}`;
  const config = {
    open: {
      title: 'Open cycle',
      body: `New applications start as set under New applications — ${channels}. Settings stay editable while it’s open; anything applicants’ records already depend on stays as it is.`,
      cta: 'Open cycle',
      ctaColor: 'success' as const,
      severity: 'info' as const,
    },
    close: {
      title: 'Close cycle',
      body: 'The admission season ends: no new applications online or at the desk. Applicants already in the cycle are still processed — tests, offers, fees and enrollment carry on. You can reopen it any time before archiving. To stop new applications only for a while, pause them under New applications instead.',
      cta: 'Close cycle',
      ctaColor: 'warning' as const,
      severity: 'warning' as const,
    },
    reopen: {
      title: 'Reopen cycle',
      body: `New applications resume as they were when the cycle closed — ${channels}. The public link is the same one as before.`,
      cta: 'Reopen cycle',
      ctaColor: 'success' as const,
      severity: 'info' as const,
    },
    archive: {
      title: 'Archive cycle',
      body: 'The cycle is put away for good: it can’t be reopened and its public link stops working permanently. Its applications stay in reports.',
      cta: 'Archive cycle',
      ctaColor: 'inherit' as const,
      severity: 'warning' as const,
    },
    delete: {
      title: 'Delete this draft',
      body: 'Drafts have no applications attached, so deletion is safe. Only Draft cycles can be deleted — opened or closed cycles must be archived instead.',
      cta: 'Delete draft',
      ctaColor: 'error' as const,
      severity: 'error' as const,
    },
  }[action];

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" fontWeight={700}>{config.title}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">{config.body}</Typography>
          <Alert severity={config.severity} variant="outlined" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              <b>{cycle.name}</b>
            </Typography>
          </Alert>
          {!!problems?.length && (
            <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Fix these in the cycle's settings first</Typography>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.25 }}>
                {problems.map(p => <li key={p}><Typography variant="body2">{p}</Typography></li>)}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color={config.ctaColor}
          disableElevation
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Working…' : config.cta}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CycleActionsConfirmDialog;
